import { useSetAtom } from 'jotai';
import {
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@carbon/react';
import { Location, TrashCan } from '@carbon/react/icons';
import { ColorSwatch, hexToRgba } from 'components/color-picker';
import { appStore } from 'components/map';
import { type CoordinatePin, recolorPinAtom, removePinAtom } from './atoms';
import { PinCopyButton } from './copy-feedback';
import { usePinActions } from './hooks';
import { formatDecimalPair } from './coordinate-utils';
import styles from './coordinate-pins.module.scss';

/**
 * The card's dropped pins, one row each: the colour to recolor, the well or place
 * it marks (or its coordinates), and show on map, copy and remove.
 *
 * @param {object} props - Component props.
 * @param {CoordinatePin[]} props.pins - The pins, in the order they were dropped.
 * @return {JSX.Element} The table.
 */
export function PinTable({ pins }: { pins: CoordinatePin[] }): JSX.Element {
  const removePin = useSetAtom(removePinAtom, appStore);
  const recolorPin = useSetAtom(recolorPinAtom, appStore);
  const { flyTo } = usePinActions();

  return (
    <div className={styles.pinTable}>
      <Table size="sm" aria-label="Dropped pins">
        <colgroup>
          <col />
          <col className={styles.locationCol} />
          <col className={styles.actionsCol} />
        </colgroup>
        <TableHead>
          <TableRow>
            <TableHeader>Color</TableHeader>
            <TableHeader>Location</TableHeader>
            <TableHeader>Actions</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {pins.map((pin) => (
            <TableRow key={pin.id}>
              <TableCell className={styles.swatchCell}>
                <ColorSwatch
                  color={pin.color}
                  popoverPosition="auto"
                  onChange={(hex, opacity) =>
                    recolorPin({
                      id: pin.id,
                      color: hexToRgba(hex, opacity),
                    })
                  }
                />
              </TableCell>
              <TableCell>
                {pin.label ? (
                  <span
                    className={styles.pinLabel}
                    title={formatDecimalPair(pin.lngLat)}
                  >
                    {pin.label}
                  </span>
                ) : (
                  <span className={styles.coordinates}>
                    {formatDecimalPair(pin.lngLat)}
                  </span>
                )}
              </TableCell>
              <TableCell>
                <div className={styles.rowActions}>
                  <IconButton
                    label="Show on map"
                    kind="ghost"
                    size="sm"
                    align="left"
                    onClick={() => flyTo(pin.lngLat)}
                  >
                    <Location />
                  </IconButton>
                  <PinCopyButton lngLat={pin.lngLat} />
                  <IconButton
                    label="Remove pin"
                    kind="ghost"
                    size="sm"
                    align="left"
                    onClick={() => removePin(pin.id)}
                  >
                    <TrashCan />
                  </IconButton>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
